import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, ExternalLink, Megaphone } from "lucide-react";

export default function AnnouncementBanner({ clinicId }) {
  const [anns, setAnns] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pulse_dismissed_ann") || "[]"); } catch { return []; }
  });
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!clinicId) return;
    api.get(`/clinics/${clinicId}/announcements`).then((r) => setAnns(r.data || []));
  }, [clinicId]);

  const visible = anns.filter((a) => !dismissed.includes(a.id));

  useEffect(() => {
    if (visible.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % visible.length), 7000);
    return () => clearInterval(t);
  }, [visible.length]);

  if (!visible.length) return null;
  const a = visible[idx % visible.length];

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("pulse_dismissed_ann", JSON.stringify(next));
  };

  const isPoster = a.type === "poster" && a.image_url;

  return (
    <div className="max-w-[1500px] mx-auto px-6 mt-4">
      <div
        className={`relative rounded-2xl overflow-hidden shadow-3d fade-up ${isPoster ? "" : "bg-gradient-to-r from-[#004a99] via-[#0066d6] to-[#004a99] text-white"}`}
        data-testid={`announcement-banner-${a.id}`}
      >
        {isPoster && (
          <img src={a.image_url} alt={a.title} className="w-full max-h-[260px] object-cover" />
        )}
        {!isPoster && (
          <div className="px-6 py-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-white/15 ring-1 ring-white/30 flex items-center justify-center">
              <Megaphone className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-semibold text-lg leading-tight">{a.title}</div>
              {a.body && <div className="text-white/80 text-sm mt-0.5">{a.body}</div>}
            </div>
            {a.cta_label && a.cta_url && (
              <a href={a.cta_url} target="_blank" rel="noreferrer"
                 className="bg-[#f37021] hover:bg-[#d9621b] text-white font-semibold text-sm px-4 py-2 rounded-lg transition flex items-center gap-1"
                 data-testid={`ann-cta-${a.id}`}>
                {a.cta_label} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button onClick={() => dismiss(a.id)} className="text-white/70 hover:text-white p-1" data-testid={`ann-dismiss-${a.id}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {visible.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {visible.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#f37021]" : "w-1.5 bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
